import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row
} from 'react-bootstrap';
import {
  FileText,
  Heart,
  MessageSquare,
  Pencil,
  Play,
  Plus,
  Search,
  Star,
  Trash2,
  X
} from 'lucide-react';
import type { MultiValue } from 'react-select';
import Flex from '@/components/common/Flex';
import MultiSelect from '@/components/common/MultiSelect';
import SubtleBadge from '@/components/common/SubtleBadge';
import CancelModal from '@/app/common/components/CancelModal';
import DataLoadError from '@/components/errors/DataLoadError';
import { errorMessage, successMessage } from '@/utils/toast';
import {
  toMcpApiErrorMessage,
  useDeleteMcpPromptTemplateMutation,
  useLikeMcpPromptTemplateMutation,
  useListMcpPromptCategoriesQuery,
  useListMcpPromptTemplatesQuery,
  useListMcpToolTypesQuery
} from '../api/mcpApi';
import type {
  ListMcpPromptTemplatesArgs,
  McpPromptListSort,
  McpPromptTemplate
} from '../types/mcpApiTypes';
import { PromptTemplateFormModal } from '../components/PromptTemplateFormModal';
import { McpPromptsListPreload } from '../components/McpPromptsListPreload';
import {
  STATUS_LABELS,
  VISIBILITY_LABELS
} from '../config/promptTemplateOptions';
import { buildPromptTemplateUserMessage } from '../utils/promptTemplateMarkers';

type ScopeFilter = 'library' | 'mine';
type PendingDelete = { id: string; title: string };
type SelectOption = { value: string; label: string };

const SORT_OPTIONS: Array<{ value: McpPromptListSort; label: string }> = [
  { value: 'newest', label: 'جدیدترین' },
  { value: 'most_used', label: 'بیشترین استفاده' },
  { value: 'most_liked', label: 'بیشترین لایک' }
];

const SELECT_STYLES = {
  container: (base: Record<string, unknown>) => ({
    ...base,
    width: '100%',
    display: 'block'
  }),
  control: (base: Record<string, unknown>) => ({
    ...base,
    width: '100%',
    minHeight: 31,
    fontSize: '0.8125rem',
    boxShadow: 'none',
    cursor: 'pointer',
    flexWrap: 'nowrap'
  }),
  valueContainer: (base: Record<string, unknown>) => ({
    ...base,
    padding: '0 8px',
    gap: 4,
    flexWrap: 'nowrap'
  }),
  multiValue: (base: Record<string, unknown>) => ({
    ...base,
    margin: 0,
    flexShrink: 0
  }),
  indicatorsContainer: (base: Record<string, unknown>) => ({
    ...base,
    height: 31,
    flexShrink: 0
  }),
  dropdownIndicator: (base: Record<string, unknown>) => ({
    ...base,
    padding: '4px 8px'
  }),
  clearIndicator: (base: Record<string, unknown>) => ({
    ...base,
    padding: '4px 8px'
  }),
  menu: (base: Record<string, unknown>) => ({
    ...base,
    fontSize: '0.8125rem',
    zIndex: 5,
    minWidth: '100%',
    width: 'max-content'
  })
};

function multiSelectAutoWidth(
  selected: SelectOption[],
  minWidth = 180,
  maxWidth = 480
): number {
  if (selected.length === 0) return minWidth;
  const chipsWidth = selected.reduce((sum, option) => {
    const labelWidth = Math.min(option.label.length, 14) * 8;
    return sum + labelWidth + 36;
  }, 0);
  return Math.min(maxWidth, Math.max(minWidth, chipsWidth + 56));
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

function statusSubtleBg(status: string): string {
  if (status === 'approved') return 'success';
  if (status === 'pending') return 'warning';
  if (status === 'rejected') return 'danger';
  if (status === 'draft') return 'secondary';
  return 'primary';
}

export default function McpPromptsPage() {
  const navigate = useNavigate();
  const [scope, setScope] = useState<ScopeFilter>('library');
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query, 300);
  const [selectedCategories, setSelectedCategories] = useState<SelectOption[]>(
    []
  );
  const [selectedTools, setSelectedTools] = useState<SelectOption[]>([]);
  const [sort, setSort] = useState<McpPromptListSort>('newest');

  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | false>(
    false
  );
  const [likingId, setLikingId] = useState<string | null>(null);
  const [optimisticItems, setOptimisticItems] = useState<
    McpPromptTemplate[] | null
  >(null);

  const listArgs = useMemo<ListMcpPromptTemplatesArgs>(
    () => ({
      limit: 50,
      offset: 0,
      scope,
      sort,
      filters: {
        ...(debouncedQuery.trim() ? { q: debouncedQuery.trim() } : {}),
        ...(selectedCategories.length
          ? { categoryIds: selectedCategories.map(option => option.value) }
          : {}),
        ...(selectedTools.length
          ? { toolIds: selectedTools.map(option => option.value) }
          : {})
      }
    }),
    [debouncedQuery, scope, selectedCategories, selectedTools, sort]
  );

  const {
    data: listData,
    isFetching,
    isError,
    error: listError,
    refetch
  } = useListMcpPromptTemplatesQuery(listArgs);
  const { data: categories = [] } = useListMcpPromptCategoriesQuery();
  const { data: tools = [] } = useListMcpToolTypesQuery();
  const [likeTemplate] = useLikeMcpPromptTemplateMutation();
  const [deleteTemplate, { isLoading: isDeleting }] =
    useDeleteMcpPromptTemplateMutation();

  const categoryOptions = useMemo(
    () => categories.map(row => ({ value: row.id, label: row.title })),
    [categories]
  );
  const toolOptions = useMemo(
    () => tools.map(row => ({ value: row.id, label: row.title })),
    [tools]
  );

  const serverItems = listData?.items ?? [];
  const items = optimisticItems ?? serverItems;
  const total = listData?.total ?? 0;
  const loading = isFetching && !listData;
  const error = isError
    ? toMcpApiErrorMessage(listError, 'بارگذاری ناموفق بود')
    : null;

  useEffect(() => {
    setOptimisticItems(null);
  }, [serverItems]);

  const openCreate = () => {
    setEditId(null);
    setFormOpen(true);
  };

  const openEdit = (item: McpPromptTemplate) => {
    if (item.hasPendingUpdate && !item.parentId) {
      const pending = items.find(
        row => row.parentId === item.id && row.isOwner
      );
      setEditId(pending?.id ?? item.id);
    } else {
      setEditId(item.id);
    }
    setFormOpen(true);
  };

  const useInAssistant = (item: McpPromptTemplate) => {
    navigate('/chat', {
      state: {
        mcpNewChat: Date.now(),
        initialPrompt: buildPromptTemplateUserMessage({
          id: item.id,
          title: item.title
        })
      }
    });
  };

  const toggleLike = async (item: McpPromptTemplate) => {
    if (likingId) return;
    setLikingId(item.id);
    const prevLiked = item.likedByMe;
    const prevCount = item.likesCount;
    const baseItems = items;
    setOptimisticItems(
      baseItems.map(row =>
        row.id === item.id
          ? {
              ...row,
              likedByMe: !prevLiked,
              likesCount: Math.max(0, prevCount + (prevLiked ? -1 : 1))
            }
          : row
      )
    );
    try {
      const result = await likeTemplate(item.id).unwrap();
      setOptimisticItems(
        baseItems.map(row =>
          row.id === item.id
            ? {
                ...row,
                likedByMe: result.likedByMe,
                likesCount: result.likesCount
              }
            : row
        )
      );
    } catch (err) {
      setOptimisticItems(
        baseItems.map(row =>
          row.id === item.id
            ? { ...row, likedByMe: prevLiked, likesCount: prevCount }
            : row
        )
      );
      errorMessage(toMcpApiErrorMessage(err, 'لایک ناموفق بود'));
    } finally {
      setLikingId(null);
    }
  };

  const addCategoryFilter = (category: { id: string; title: string }) => {
    setSelectedCategories(prev => {
      if (prev.some(option => option.value === category.id)) return prev;
      return [...prev, { value: category.id, label: category.title }];
    });
  };

  const addToolFilter = (tool: { id: string; name: string }) => {
    setSelectedTools(prev => {
      if (prev.some(option => option.value === tool.id)) return prev;
      return [...prev, { value: tool.id, label: tool.name }];
    });
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteTemplate(pendingDelete.id).unwrap();
      successMessage('قالب حذف شد');
      setPendingDelete(false);
    } catch (err) {
      errorMessage(toMcpApiErrorMessage(err, 'حذف ناموفق بود'));
    }
  };

  const hasQuery = debouncedQuery.trim().length > 0;
  const hasFilters =
    selectedCategories.length > 0 || selectedTools.length > 0;
  const resultLabel = useMemo(() => {
    if (loading) return 'در حال بارگذاری…';
    if (hasQuery || hasFilters) {
      return total > 0
        ? `${total.toLocaleString('fa-IR')} نتیجه`
        : 'نتیجه‌ای پیدا نشد';
    }
    return total > 0
      ? `${total.toLocaleString('fa-IR')} قالب`
      : 'قالبی یافت نشد';
  }, [hasFilters, hasQuery, loading, total]);

  return (
    <>
      <CancelModal
        show={Boolean(pendingDelete)}
        setShow={setPendingDelete}
        title="حذف قالب"
        submitTitle="آره، حذف بشه"
        isSubmitting={isDeleting}
        submitFunc={confirmDelete}
        buttonSize="sm"
        desc={
          <>
            آیا مطمئن هستید می‌خواهید قالب{' '}
            <strong>{pendingDelete ? pendingDelete.title : ''}</strong> را حذف
            کنید؟
          </>
        }
      />

      <PromptTemplateFormModal
        show={formOpen}
        editId={editId}
        onHide={() => setFormOpen(false)}
        onSaved={saved => {
          setFormOpen(false);
          successMessage(
            saved.parentId
              ? 'نسخه ویرایش برای تایید ثبت شد'
              : editId
                ? 'قالب به‌روز شد'
                : 'قالب ایجاد شد'
          );
          void refetch();
        }}
      />

      {!loading && error ? (
        <DataLoadError
          className="mb-3"
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          isNetworkError={/failed to fetch|network|internet|اتصال/i.test(
            error
          )}
        />
      ) : (
        <>
          <Card className="mb-3">
            <Card.Body className="py-3">
              <Flex
                alignItems="start"
                justifyContent="between"
                wrap="wrap"
                className="gap-3 mb-3"
              >
                <div className="min-w-0">
                  <div className="fs--2 text-500 mb-1">دستیار AI</div>
                  <h5 className="mb-1">قالب‌های پرامپت</h5>
                  <p className="mb-0 fs--1 text-600">
                    قالب‌های آماده تحلیل ردیاب و تحقیق کلمات را ببینید یا خودتان
                    بسازید.
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="primary"
                  className="flex-shrink-0"
                  onClick={openCreate}
                >
                  <Plus size={15} strokeWidth={2.25} className="me-1" />
                  قالب جدید
                </Button>
              </Flex>

              <Flex
                alignItems="center"
                wrap="wrap"
                className="gap-2"
              >
                <Flex className="gap-2 flex-wrap flex-shrink-0">
                  {(
                    [
                      ['library', 'قالب‌ها'],
                      ['mine', 'قالب‌های من']
                    ] as const
                  ).map(([value, label]) => (
                    <Button
                      key={value}
                      size="sm"
                      variant={
                        scope === value ? 'primary' : 'outline-secondary'
                      }
                      onClick={() => setScope(value)}
                    >
                      {label}
                    </Button>
                  ))}
                </Flex>

                <InputGroup size="sm" style={{ width: 220, maxWidth: '100%' }}>
                  <InputGroup.Text className="bg-body py-0">
                    <Search size={14} strokeWidth={2} className="text-500" />
                  </InputGroup.Text>
                  <Form.Control
                    type="search"
                    value={query}
                    onChange={event => setQuery(event.target.value)}
                    placeholder="جستجو…"
                    autoComplete="off"
                  />
                  {query ? (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setQuery('')}
                      aria-label="پاک کردن جستجو"
                    >
                      <X size={13} strokeWidth={2.25} />
                    </Button>
                  ) : null}
                </InputGroup>

                <div
                  className="flex-shrink-0"
                  style={{
                    width: multiSelectAutoWidth(selectedCategories),
                    maxWidth: '100%',
                    transition: 'width 0.15s ease'
                  }}
                >
                  <MultiSelect
                    options={categoryOptions}
                    value={selectedCategories}
                    onChange={value =>
                      setSelectedCategories([
                        ...((value as MultiValue<SelectOption>) ?? [])
                      ])
                    }
                    placeholder="دسته…"
                    isClearable
                    isSearchable
                    hideSelectedOptions={false}
                    styles={SELECT_STYLES as never}
                  />
                </div>

                <div
                  className="flex-shrink-0"
                  style={{
                    width: multiSelectAutoWidth(selectedTools),
                    maxWidth: '100%',
                    transition: 'width 0.15s ease'
                  }}
                >
                  <MultiSelect
                    options={toolOptions}
                    value={selectedTools}
                    onChange={value =>
                      setSelectedTools([
                        ...((value as MultiValue<SelectOption>) ?? [])
                      ])
                    }
                    placeholder="ابزار…"
                    isClearable
                    isSearchable
                    hideSelectedOptions={false}
                    styles={SELECT_STYLES as never}
                  />
                </div>

                <Form.Select
                  size="sm"
                  value={sort}
                  onChange={event =>
                    setSort(event.target.value as McpPromptListSort)
                  }
                  style={{ width: 160, maxWidth: '100%' }}
                  aria-label="مرتب‌سازی"
                >
                  {SORT_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>

                <span className="fs--2 text-500 ms-auto">{resultLabel}</span>
              </Flex>
            </Card.Body>
          </Card>

          {loading && items.length === 0 ? (
            <McpPromptsListPreload cards={6} />
          ) : null}

          {!loading && items.length === 0 ? (
            <Card className="mb-3">
              <Card.Body className="text-center py-5 px-3">
                <div className="d-inline-flex align-items-center justify-content-center text-500 mb-3">
                  <FileText size={22} strokeWidth={1.75} />
                </div>
                <h5 className="fs-0 mb-2">
                  {hasQuery ? 'نتیجه‌ای پیدا نشد' : 'هنوز قالبی نیست'}
                </h5>
                <p
                  className="mb-3 fs--1 text-600 mx-auto"
                  style={{ maxWidth: 340 }}
                >
                  {hasQuery
                    ? 'عبارت دیگری را امتحان کنید.'
                    : 'اولین قالب تحلیل را بسازید؛ بعد از تایید در کتابخانه دیده می‌شود.'}
                </p>
                {!hasQuery ? (
                  <Button size="sm" variant="primary" onClick={openCreate}>
                    <Plus size={15} strokeWidth={2.25} className="me-1" />
                    ساخت قالب
                  </Button>
                ) : null}
              </Card.Body>
            </Card>
          ) : null}

          {items.length > 0 ? (
            <Row className="g-3 mb-3">
              {items.map(item => (
                <Col key={item.id} xs={12} md={6} xl={4}>
                  <Card className="h-100">
                    <Card.Body className="d-flex flex-column p-3">
                      <Flex
                        alignItems="start"
                        justifyContent="between"
                        className="gap-2 mb-2"
                      >
                        <div className="min-w-0">
                          <Flex
                            alignItems="center"
                            className="gap-1 flex-wrap mb-1"
                          >
                            {item.isFeatured ? (
                              <Star
                                size={13}
                                className="text-warning flex-shrink-0"
                                fill="currentColor"
                                aria-label="ویژه"
                              />
                            ) : null}
                            <h6 className="mb-0 fs--1 fw-semi-bold text-truncate">
                              {item.title}
                            </h6>
                          </Flex>
                          {(item.parentId ||
                            item.hasPendingUpdate ||
                            item.status !== 'approved' ||
                            item.visibility !== 'public') && (
                            <Flex className="gap-1 flex-wrap">
                              {item.status !== 'approved' ? (
                                <SubtleBadge
                                  bg={statusSubtleBg(item.status)}
                                  pill
                                  className="fs--2"
                                >
                                  {STATUS_LABELS[item.status] ?? item.status}
                                </SubtleBadge>
                              ) : null}
                              {item.visibility !== 'public' ? (
                                <SubtleBadge
                                  bg="secondary"
                                  pill
                                  className="fs--2"
                                >
                                  {VISIBILITY_LABELS[item.visibility] ??
                                    item.visibility}
                                </SubtleBadge>
                              ) : null}
                              {item.parentId ? (
                                <SubtleBadge bg="info" pill className="fs--2">
                                  نسخه ویرایش
                                </SubtleBadge>
                              ) : null}
                              {item.hasPendingUpdate ? (
                                <SubtleBadge bg="warning" pill className="fs--2">
                                  در صف تایید
                                </SubtleBadge>
                              ) : null}
                            </Flex>
                          )}
                        </div>

                        {item.isOwner ? (
                          <Flex className="gap-1 flex-shrink-0">
                            <Button
                              size="sm"
                              variant="falcon-default"
                              className="px-2"
                              onClick={() => openEdit(item)}
                              title="ویرایش"
                              aria-label={`ویرایش ${item.title}`}
                            >
                              <Pencil size={13} strokeWidth={2} />
                            </Button>
                            <Button
                              size="sm"
                              variant="falcon-danger"
                              className="px-2"
                              onClick={() =>
                                setPendingDelete({
                                  id: item.id,
                                  title: item.title
                                })
                              }
                              title="حذف"
                              aria-label={`حذف ${item.title}`}
                            >
                              <Trash2 size={13} strokeWidth={2} />
                            </Button>
                          </Flex>
                        ) : null}
                      </Flex>

                      <p
                        className="fs--1 text-600 mb-2 flex-grow-1"
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {item.description?.trim() || 'بدون توضیح'}
                      </p>

                      <div className="mt-auto">
                        <div className="mb-2 fs--2">
                          <span className="text-500">دسته‌ها: </span>
                          {(item.categories?.length ?? 0) > 0 ? (
                            item.categories!.map((category, index) => (
                              <span key={category.id}>
                                {index > 0 ? (
                                  <span className="text-400 mx-1">|</span>
                                ) : null}
                                <button
                                  type="button"
                                  className="btn btn-link p-0 align-baseline fs--2 text-primary text-decoration-none"
                                  onClick={() => addCategoryFilter(category)}
                                >
                                  {category.title}
                                </button>
                              </span>
                            ))
                          ) : (
                            <span className="text-400">—</span>
                          )}
                        </div>

                        <div className="fs--2">
                          <span className="text-500">ابزارها: </span>
                          {(item.tool_names?.length ?? 0) > 0 ? (
                            item.tool_names!.map((tool, index) => (
                              <span key={tool.id}>
                                {index > 0 ? (
                                  <span className="text-400 mx-1">|</span>
                                ) : null}
                                <button
                                  type="button"
                                  className="btn btn-link p-0 align-baseline fs--2 text-primary text-decoration-none"
                                  onClick={() => addToolFilter(tool)}
                                >
                                  {tool.name}
                                </button>
                              </span>
                            ))
                          ) : (
                            <span className="text-400">—</span>
                          )}
                        </div>
                      </div>
                    </Card.Body>

                    <Card.Footer className="bg-body-tertiary border-top py-2 px-3">
                      <Flex
                        alignItems="center"
                        justifyContent="between"
                        wrap="wrap"
                        className="gap-2"
                      >
                        <Flex alignItems="center" className="gap-2 flex-wrap">
                          <span
                            className="d-inline-flex align-items-center gap-1 fs--2 text-500"
                            title="تعداد استفاده"
                          >
                            <Play size={12} strokeWidth={2} />
                            <span>
                              {(item.usageCount ?? 0).toLocaleString('fa-IR')}
                            </span>
                          </span>

                          <span className="fs--2 text-600">
                            طراحی:{' '}
                            {item.source === 'system'
                              ? 'میزفاتولز'
                              : item.userName?.trim() || '—'}
                          </span>
                        </Flex>

                        <Flex alignItems="center" className="gap-2 flex-shrink-0">
                          <Button
                            size="sm"
                            variant={
                              item.likedByMe ? 'falcon-danger' : 'falcon-default'
                            }
                            className="fs--2 px-2"
                            disabled={likingId === item.id}
                            onClick={() => void toggleLike(item)}
                            title={item.likedByMe ? 'برداشتن لایک' : 'لایک'}
                            aria-label={
                              item.likedByMe
                                ? `برداشتن لایک ${item.title}`
                                : `لایک ${item.title}`
                            }
                            aria-pressed={item.likedByMe}
                          >
                            <Heart
                              size={12}
                              strokeWidth={2}
                              className="me-1"
                              fill={item.likedByMe ? 'currentColor' : 'none'}
                            />
                            {(item.likesCount ?? 0).toLocaleString('fa-IR')}
                          </Button>
                          <Button
                            size="sm"
                            variant="falcon-primary"
                            className="fs--2"
                            onClick={() => useInAssistant(item)}
                          >
                            <MessageSquare
                              size={12}
                              strokeWidth={2}
                              className="me-1"
                            />
                            استفاده در دستیار
                          </Button>
                        </Flex>
                      </Flex>
                    </Card.Footer>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : null}
        </>
      )}
    </>
  );
}
